import React, { useEffect, useState } from 'react';

import { AuthSession } from 'aws-amplify/auth';
import { getAuthConfig } from 'src/api/auth';
import { Loader } from 'src/components/atoms/Loader';
import { useAuth } from 'src/components/hooks/useAuth';
import { AuthProviderError } from 'src/components/organisms/AuthProviderError';
import { AuthProviderHelp } from 'src/components/organisms/AuthProviderHelp';
import { ProfileMenu } from 'src/components/ProfileMenu';
import { AuthConfig } from 'src/dto/auth/AuthConfig';
import { AUTH_PROVIDER_KEY } from 'src/pages/AuthProvider/constants';
import { Login } from 'src/pages/Login';
import {
  authProviderErrorTestId,
  authProviderHelpTestId,
  userProviderLoaderTestId,
  userProviderLoginTestId,
} from './constants';
import { UserContext } from './UserContext';

export type UserState = {
  user: User;
  api: {
    signIn: () => Promise<void>;
    signOut: () => Promise<void>;
    getUser: () => Promise<User>;
  };
};

export type User = {
  firstName: string;
  email: string;
  accessToken: string;
};

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null);
  const provider = localStorage.getItem(AUTH_PROVIDER_KEY);

  useEffect(() => {
    async function fetchAuthConfig(provider: string) {
      try {
        const config = await getAuthConfig(provider);
        setAuthConfig(config);
        setIsLoading(false);
      } catch (error) {
        //@ts-expect-error AUTOMATICALLY GENERATED PLS FIX
        setError(error.message);
      }
    }

    if (provider) {
      fetchAuthConfig(provider);
    }
  }, [provider]);

  const {
    session,
    isSignedIn,
    api: { signIn, signOut, getUserSession },
  } = useAuth(authConfig);

  const getUserData = (session: AuthSession): User => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload: any = session.tokens?.idToken?.payload;
    return {
      firstName: payload.given_name,
      email: payload.email,
      accessToken: session.tokens!.accessToken.toString(),
    };
  };

  const getUser = async () => {
    const session = await getUserSession();
    return getUserData(session);
  };

  if (!provider) {
    return <AuthProviderHelp testId={authProviderHelpTestId} />;
  }

  if (isSignedIn) {
    const signedInSession = session!;
    const user = getUserData(signedInSession);

    return (
      <UserContext.Provider
        value={{
          user,
          api: {
            signIn,
            signOut,
            getUser,
          },
        }}
      >
        <ProfileMenu name={user.firstName} onSignOut={signOut} />
        {children}
      </UserContext.Provider>
    );
  }

  if (error) {
    return <AuthProviderError testId={authProviderErrorTestId} />;
  }

  return isLoading ? (
    <Loader data-testid={userProviderLoaderTestId} cover />
  ) : (
    <Login data-testid={userProviderLoginTestId} onSignIn={signIn} />
  );
}
